import React, { useCallback, useState } from 'react';

import { CardControlPanel as CardControlPanelV3 } from '@eika-kortbetjening/card-control-panel';
import { ColumnDividerBox } from '@eika-kortbetjening/column-divider-box';
import { InfoMessage } from '@eika/attention-message';
import { LinkButton } from '@eika/button';

import { KbjCardService } from '@/domain/card';
import piwik from '@/piwik/piwik';
import { useDebitAccountByIdQuery } from '@/react-query/hooks/query/accounts/use-debit-accounts';
import { useCard } from '@/react-query/hooks/query/use-card';

import { useSession } from '@components/app/Session';
import { ModalState } from '@components/card/card-info-with-functionality/CardInfoWithFunctionality';
import Functionality from '@components/card/card-info-with-functionality/functionality/Functionality';
import Modals from '@components/card/card-info-with-functionality/modals/Modals';
import { CouldNotGetCardDetailsErrorCard } from '@components/error/ErrorCard';
import LoaderWithText from '@components/loading/LoaderWithText';
import { Card, CardActivation, CardType, DebitAccount } from '@generated-types';
import { useIsHighFraudRisk } from '@hooks/use-is-high-risk';
import useText from '@hooks/use-text';
import { canReopen } from '@utils/allowed-operations/card-allowed-operations-utils';
import { getBankCardName } from '@utils/card-utils';

type CardInfoWithFunctionalityContainerProps = {
    card: Card;
    debitAccount?: DebitAccount;
};

const CardInfoWithFunctionalityContainer = ({ card, debitAccount }: CardInfoWithFunctionalityContainerProps) => {
    const session = useSession();
    const isHighRisk = useIsHighFraudRisk();

    const text = useText();

    const isAllowedToViewCardsPin = card.allowedOperations.getPin;
    const isAllowedToViewVirtualCards = card.allowedOperations.getVirtualCard;

    const [modalState, setModalState] = useState<ModalState>({
        showDigitalCardModal: false,
        showPinModal: false,
        showReopenCardModal: false,
        showCardActivationModal: false,
    });

    const onClickPin = useCallback(() => {
        if (!card) {
            return;
        }

        setModalState({ ...modalState, showPinModal: true });
    }, [card, modalState]);

    const openDigitalCardModal = useCallback(() => {
        if (!card) {
            return;
        }

        setModalState({ ...modalState, showDigitalCardModal: true });
    }, [card, modalState]);

    const openReopenCardModal = useCallback(() => {
        if (!card) {
            return;
        }

        setModalState({ ...modalState, showReopenCardModal: true });
    }, [modalState, setModalState, card]);

    const openCardActivationModal = useCallback(() => {
        if (!card) {
            return;
        }

        setModalState({ ...modalState, showCardActivationModal: true });
    }, [modalState, setModalState, card]);

    const [kbjCard, kbjCardMeta] = KbjCardService.toKbjPanel(
        card,
        session,
        getBankCardName(card, text, debitAccount),
        () => '',
    );

    const canReopenCard = canReopen(card);
    const disableActivationMessage = (flag?: CardActivation): boolean =>
        flag === CardActivation.MUST_ACTIVATE_IN_ATM || flag === CardActivation.ACTIVE;

    return (
        <ColumnDividerBox>
            <ColumnDividerBox.Item noDivider>
                <CardControlPanelV3
                    card={kbjCard}
                    cardMeta={kbjCardMeta}
                    onDigitalCardClick={
                        (isAllowedToViewVirtualCards && !isHighRisk && openDigitalCardModal) || undefined
                    }
                    onSeePinClick={(isAllowedToViewCardsPin && onClickPin) || undefined}
                    reopenCardClick={canReopenCard ? openReopenCardModal : undefined}
                    piwik={piwik}
                />
                <Modals card={card} modalState={modalState} setModalState={setModalState} />
            </ColumnDividerBox.Item>
            {!disableActivationMessage(card.activation) && (
                <ColumnDividerBox.Item noDivider>
                    <InfoMessage
                        type="default"
                        text={text.activateInfoBox1}
                        clickableItem={<LinkButton onClick={openCardActivationModal}>Aktiver kort</LinkButton>}
                    />
                </ColumnDividerBox.Item>
            )}
            <ColumnDividerBox.Item noPadding>
                <Functionality card={card} />
            </ColumnDividerBox.Item>
        </ColumnDividerBox>
    );
};

const CardQuery = () => {
    const { cardInformationFetchErrorMessage } = useText();

    const cardQuery = useCard();
    const accountQueryEnabled = cardQuery.data?.type === CardType.DEBIT;
    const accountQuery = useDebitAccountByIdQuery(cardQuery.data?.accountId, {
        enabled: accountQueryEnabled,
    });

    if (cardQuery.isError) {
        return (
            <CouldNotGetCardDetailsErrorCard
                tryAgainClick={() => {
                    if (cardQuery.isError) {
                        cardQuery.refetch();
                    }
                    if (accountQuery.isError) {
                        accountQuery.refetch();
                    }
                }}
                errorMessageText={cardInformationFetchErrorMessage}
            />
        );
    }

    if (cardQuery.isLoading || accountQuery.isLoading) {
        return <LoaderWithText title="Laster kort..." />;
    }

    if (cardQuery.isPending || (accountQueryEnabled && accountQuery.isPending)) {
        return null;
    }

    return <CardInfoWithFunctionalityContainer card={cardQuery.data} debitAccount={accountQuery.data} />;
};

export default CardQuery;
